import React, { useEffect, useState, useRef, useCallback } from "react";
import { AppConfig } from "../../config";
import { api } from "../../utils/Api";
import { useParams } from "react-router-dom";
import { AppHelper } from "../../utils/Helper";

const ProductSelectorComponent: React.FC<{
  productData?: any;
  onSelect?: (data: any) => void;
}> = ({ productData, onSelect }) => {
  const [products, setProducts] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const [id_value] = useState(useParams());
  const [sort, setSort] = useState("");
  const [filters, setFilters] = useState({
    own: true,
    other: false,
    categories: [] as string[],
  });
  const [baseImageUrl, setBaseImageUrl] = useState("");
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
      setProducts([]);
      setHasMore(true);
    }, 500);
  };

  const fetchProducts = async (pageNum: number, searchTerm = "") => {
    setLoading(true);
    const payload = {
      get: {
        brand: id_value.brand_id,
        group_id: productData.product_group_id,
        page: pageNum,
        search: searchTerm,
        sort,
        filter_min_mrp: 0,
        filter_max_mrp: 1000000,
        filter_count: 20,
        filter_products: JSON.stringify({
          own_products: filters.own,
          others_products: filters.other,
        }),
        filter_categories: encodeURIComponent(JSON.stringify(filters.categories)),
      },
    };

    try {
      const response: any = await api("/brand/brand_variant_list", payload);
      if (response.status_code === 200 && response.response?.data) {
        if (response.response?.image_base_url) {
          setBaseImageUrl(response.response?.image_base_url);
        }
        setHasMore(response.response?.next_page ?? false);
        return response.response.data;
      }
    } catch (err) {
      console.error("API fetch error:", err);
    }
    return [];
  };

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    const newProducts = await fetchProducts(page, debouncedSearch);
    if (newProducts.length > 0) {
      setProducts((prev) => [...prev, ...newProducts]);
      setPage((prev) => prev + 1);
    }
    setLoading(false);
  }, [page, debouncedSearch, sort, filters, loading, hasMore]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) loadMore();
    }, { threshold: 1 });
    const current = loaderRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [loadMore]);

  const toggleFilter = (key: "own" | "other") => {
    setFilters((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      setPage(1);
      setProducts([]);
      setHasMore(true);
      return updated;
    });
  };

  return (
    <div className="card h-100 shadow-sm radius-10">
      {/* Search & Filters */}
      <div className="p-3 border-bottom">
        <input
          type="text"
          className="form-control mb-3"
          placeholder="Search products..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <div className="d-flex gap-3 flex-wrap align-items-center">
          <label className="form-check-label">
            <input
              type="checkbox"
              className="form-check-input me-1"
              checked={filters.own}
              onChange={() => toggleFilter("own")}
            /> Own
          </label>
          <label className="form-check-label">
            <input
              type="checkbox"
              className="form-check-input me-1"
              checked={filters.other}
              onChange={() => toggleFilter("other")}
            /> Other
          </label>
          <select
            className="form-select form-select-sm w-auto"
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              setPage(1);
              setProducts([]);
              setHasMore(true);
            }}
          >
            <option value="">Sort</option>
            <option value="asc">Price Low to High</option>
            <option value="desc">Price High to Low</option>
          </select>
        </div>
      </div>

      {/* Product List */}
      <div style={{ maxHeight: "60vh", overflowY: "auto" }} className="p-2">
        {products.length === 0 && loading ? (
          <div className="row g-2">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div className="col-6 col-md-4" key={`skel-${idx}`}>
                <div className="border rounded-3 p-2">
                  <div className="bg-light w-100 mb-2 rounded shimmer" style={{ height: "80px" }}></div>
                  <div className="bg-light rounded mb-1 shimmer" style={{ height: "14px", width: "80%" }}></div>
                  <div className="bg-light rounded shimmer" style={{ height: "10px", width: "60%" }}></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="row g-2">
            {products.map((prod) => (
              !prod.linked && (
                <div
                  key={prod.id}
                  className="col-6 col-md-4"
                  onClick={() => onSelect?.(prod)}
                  role="button"
                >
                  <div className="border rounded-3 p-2 h-100 d-flex flex-column align-items-center text-center">
                    <div
                      className="w-100 mb-2 rounded position-relative"
                      style={{ height: "80px", backgroundColor: "#f0f0f0" }}
                    >
                      {prod.images?.length > 0 && (
                        <img
                          src={AppHelper.imageSrc(baseImageUrl, prod.images[0]?.url, { format: "webp", width: 100 })}
                          alt={prod.name}
                          className="img-fluid rounded position-absolute top-0 start-0 w-100 h-100"
                          style={{
                            objectFit: "contain",
                            opacity: loadedImages[prod.id] ? 1 : 0,
                            transition: "opacity 0.3s ease"
                          }}
                          onLoad={() => setLoadedImages(prev => ({ ...prev, [prod.id]: true }))}
                        />
                      )}
                    </div>
                    <div className="fw-bold small text-wrap">{prod.name}</div>
                  </div>
                </div>
              )
            ))}
          </div>
        )}

        {hasMore && (
          <div ref={loaderRef} className="text-center py-3">
            <div className="spinner-border text-secondary" role="status" style={{ width: 24, height: 24 }} />
          </div>
        )}

        {!hasMore && products.length > 0 && (
          <div className="text-center text-muted py-2">No more products</div>
        )}
      </div>
    </div>
  );
};

export default ProductSelectorComponent;